// NewKBBoard-filtering.js
import moment from 'moment';

export const filterLeads = (leads, filters) => {
  const {
    searchQuery,
    callFilter,
    tagFilter,
    sourceFilter, // Add dedicated source filter
    assigneeFilter, // Re-enabled assignee filtering
    leadOwnerFilter, // New lead owner filter
    missedFollowUpFilter,
    todayFollowUpFilter,
    appliedDateFilter
  } = filters;

  if (!leads || leads.length === 0) return [];

  // console.log('[filterLeads] Filtering leads with filters:', filters);

  return leads.filter(lead => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = lead.brideAndGroomName?.toLowerCase().includes(query);
      const matchesPhone = lead.phone?.toLowerCase().includes(query);
      const matchesEmail = lead.email?.toLowerCase().includes(query);
      const matchesLeadId = lead.leadId?.toLowerCase().includes(query);

      if (!matchesName && !matchesPhone && !matchesEmail && !matchesLeadId) {
        return false;
      }
    }

    // Call filter
    if (callFilter && callFilter !== 'all') {
      const callCount = Number(lead.callCount || 0);

      switch(callFilter) {
        case 'no_calls':
          if (callCount !== 0) return false;
          break;
        case 'never_picked_up':
          // This lead has calls but never had an answered call
          if (callCount === 0) return false; // Must have at least one call
          // Use the answeredCallCount field provided by the backend
          // This counts call logs with outcome = 'answered'
          const answeredCallCount = lead.answeredCallCount || 0;
          if (answeredCallCount > 0) return false; // Must not have any answered calls
          break;
        case 'one_call':
          if (callCount !== 1) return false;
          break;
        case 'two_calls':
          if (callCount !== 2) return false;
          break;
        case 'less_than_3':
          if (callCount > 2) return false;
          break;
        case 'less_than_5':
          if (callCount > 4) return false;
          break;
        case 'more_than_5':
          if (callCount < 5) return false;
          break;
        case 'custom_range':
          // Assuming min/max might be passed in filters in a future enhancement
          // For now, using the logic from the original component if needed,
          // though ideally the range values should be passed into this function.
          // This part might need adjustment based on how custom range is implemented.
          const urlParams = new URLSearchParams(window.location.search); // Example: Reading from URL
          const min = parseInt(urlParams.get('custom_range_min') || '0', 10);
          const max = parseInt(urlParams.get('custom_range_max') || '20', 10);
          if (callCount < min || callCount > max) return false;
          break;
        default:
          break;
      }
    }

    // Tag filter - now only checks tags, not sources
    if (tagFilter && tagFilter.length > 0) {
      if (tagFilter.includes('no_tags')) {
        // Special case: Show only leads with no tags
        if (lead.tags && lead.tags.length > 0) {
          return false;
        }
      } else {
        // Normal tag filtering - must match at least one selected tag
        const hasMatchingTag = lead.tags && lead.tags.some(tag => tagFilter.includes(tag));
        if (!hasMatchingTag) {
          return false;
        }
      }
    }
    
    // Source filter - separate from tags
    if (sourceFilter && sourceFilter.length > 0) {
      // Check if lead source matches any of the selected sources
      const matchesSource = sourceFilter.some(source => {
        // Convert display name to backend ID format (e.g., 'Website' → 'source_website')
        const sourceId = `source_${source.toLowerCase().replace(/\s+/g, '_')}`;
        
        // Direct match with the ID
        if (lead.leadSource === sourceId) return true;
        
        // Check if it's stored in a different format but relates to the same source
        if (lead.leadSource && lead.leadSource.startsWith('source_')) {
          // Convert backend ID to display format (e.g., 'source_social_media' → 'social media')
          const sourceName = lead.leadSource
            .replace('source_', '')
            .replace(/_/g, ' ');
            
          // Case-insensitive partial match
          return sourceName.toLowerCase().includes(source.toLowerCase());
        }
        
        return false;
      });
      
      if (!matchesSource) {
        return false;
      }
    }

    // Assignee filter
    if (assigneeFilter) {
      // Check if the lead is assigned to the selected user
      if (!lead.assignedTo || lead.assignedTo !== assigneeFilter) {
        return false;
      }
    }
    
    // Lead Owner filter
    if (leadOwnerFilter) {
      // Check if the lead is owned by the selected user
      if (!lead.leadOwner || lead.leadOwner !== leadOwnerFilter) {
        return false;
      }
    }

    // Missed follow-up filter
    if (missedFollowUpFilter) {
      const followUpDate = lead.nextFollowUpTime ? moment(lead.nextFollowUpTime) : null;
      if (!followUpDate || followUpDate.isAfter(moment())) {
        return false;
      }
    }

    // Today's follow-up filter
    if (todayFollowUpFilter) {
      const followUpDate = lead.nextFollowUpTime ? moment(lead.nextFollowUpTime) : null;
      if (!followUpDate) return false;

      const today = moment();
      const isToday = followUpDate.isSame(today, 'day');
      if (!isToday) {
        return false;
      }
    }

    // Date filter
    if (appliedDateFilter.startDate && appliedDateFilter.endDate) {
      const createdAt = lead.createdAt ? moment(lead.createdAt) : null;
      if (!createdAt ||
          !createdAt.isBetween(appliedDateFilter.startDate, appliedDateFilter.endDate, null, '[]')) {
        return false;
      }
    }

    return true;
  });
};